/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";
import {
  getServiceEndpointDecoder,
  getServiceEndpointEncoder,
  getVerificationMethodDecoder,
  getVerificationMethodEncoder,
  type ServiceEndpoint,
  type ServiceEndpointArgs,
  type VerificationMethod,
  type VerificationMethodArgs,
} from "../types";

export const CREATE_DID_DOCUMENT_DISCRIMINATOR = new Uint8Array([
  253, 40, 200, 239, 69, 147, 82, 182,
]);

export function getCreateDidDocumentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CREATE_DID_DOCUMENT_DISCRIMINATOR,
  );
}

export type CreateDidDocumentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountDidDocument extends string | AccountMeta<string> = string,
  TAccountController extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountDidDocument extends string
        ? WritableAccount<TAccountDidDocument>
        : TAccountDidDocument,
      TAccountController extends string
        ? WritableSignerAccount<TAccountController> &
            AccountSignerMeta<TAccountController>
        : TAccountController,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type CreateDidDocumentInstructionData = {
  discriminator: ReadonlyUint8Array;
  didString: string;
  verificationMethods: Array<VerificationMethod>;
  serviceEndpoints: Array<ServiceEndpoint>;
};

export type CreateDidDocumentInstructionDataArgs = {
  didString: string;
  verificationMethods: Array<VerificationMethodArgs>;
  serviceEndpoints: Array<ServiceEndpointArgs>;
};

export function getCreateDidDocumentInstructionDataEncoder(): Encoder<CreateDidDocumentInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["didString", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["verificationMethods", getArrayEncoder(getVerificationMethodEncoder())],
      ["serviceEndpoints", getArrayEncoder(getServiceEndpointEncoder())],
    ]),
    (value) => ({ ...value, discriminator: CREATE_DID_DOCUMENT_DISCRIMINATOR }),
  );
}

export function getCreateDidDocumentInstructionDataDecoder(): Decoder<CreateDidDocumentInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["didString", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["verificationMethods", getArrayDecoder(getVerificationMethodDecoder())],
    ["serviceEndpoints", getArrayDecoder(getServiceEndpointDecoder())],
  ]);
}

export function getCreateDidDocumentInstructionDataCodec(): Codec<
  CreateDidDocumentInstructionDataArgs,
  CreateDidDocumentInstructionData
> {
  return combineCodec(
    getCreateDidDocumentInstructionDataEncoder(),
    getCreateDidDocumentInstructionDataDecoder(),
  );
}

export type CreateDidDocumentAsyncInput<
  TAccountDidDocument extends string = string,
  TAccountController extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** DID document account with canonical PDA */
  didDocument?: Address<TAccountDidDocument>;
  /** Controller of the DID (owner) */
  controller: TransactionSigner<TAccountController>;
  /** System program for account creation */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Clock sysvar for timestamp validation */
  clock?: Address<TAccountClock>;
  didString: CreateDidDocumentInstructionDataArgs["didString"];
  verificationMethods: CreateDidDocumentInstructionDataArgs["verificationMethods"];
  serviceEndpoints: CreateDidDocumentInstructionDataArgs["serviceEndpoints"];
};

export async function getCreateDidDocumentInstructionAsync<
  TAccountDidDocument extends string,
  TAccountController extends string,
  TAccountSystemProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateDidDocumentAsyncInput<
    TAccountDidDocument,
    TAccountController,
    TAccountSystemProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CreateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountSystemProgram,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    didDocument: { value: input.didDocument ?? null, isWritable: true },
    controller: { value: input.controller ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.didDocument.value) {
    accounts.didDocument.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            100, 105, 100, 95, 100, 111, 99, 117, 109, 101, 110, 116,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.controller.value)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.controller),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getCreateDidDocumentInstructionDataEncoder().encode(
      args as CreateDidDocumentInstructionDataArgs,
    ),
    programAddress,
  } as CreateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountSystemProgram,
    TAccountClock
  >);
}

export type CreateDidDocumentInput<
  TAccountDidDocument extends string = string,
  TAccountController extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** DID document account with canonical PDA */
  didDocument: Address<TAccountDidDocument>;
  /** Controller of the DID (owner) */
  controller: TransactionSigner<TAccountController>;
  /** System program for account creation */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Clock sysvar for timestamp validation */
  clock?: Address<TAccountClock>;
  didString: CreateDidDocumentInstructionDataArgs["didString"];
  verificationMethods: CreateDidDocumentInstructionDataArgs["verificationMethods"];
  serviceEndpoints: CreateDidDocumentInstructionDataArgs["serviceEndpoints"];
};

export function getCreateDidDocumentInstruction<
  TAccountDidDocument extends string,
  TAccountController extends string,
  TAccountSystemProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateDidDocumentInput<
    TAccountDidDocument,
    TAccountController,
    TAccountSystemProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): CreateDidDocumentInstruction<
  TProgramAddress,
  TAccountDidDocument,
  TAccountController,
  TAccountSystemProgram,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    didDocument: { value: input.didDocument ?? null, isWritable: true },
    controller: { value: input.controller ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.controller),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getCreateDidDocumentInstructionDataEncoder().encode(
      args as CreateDidDocumentInstructionDataArgs,
    ),
    programAddress,
  } as CreateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountSystemProgram,
    TAccountClock
  >);
}

export type ParsedCreateDidDocumentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** DID document account with canonical PDA */
    didDocument: TAccountMetas[0];
    /** Controller of the DID (owner) */
    controller: TAccountMetas[1];
    /** System program for account creation */
    systemProgram: TAccountMetas[2];
    /** Clock sysvar for timestamp validation */
    clock: TAccountMetas[3];
  };
  data: CreateDidDocumentInstructionData;
};

export function parseCreateDidDocumentInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCreateDidDocumentInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      didDocument: getNextAccount(),
      controller: getNextAccount(),
      systemProgram: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getCreateDidDocumentInstructionDataDecoder().decode(instruction.data),
  };
}
